import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useDndScrolling } from 'react-dnd-scrolling'

import CustomFieldOption from '../../CustomFieldOption'

import useBaseStyles from '../styles'
import useStyles from './styles'
import { useAppSelector } from '../../../../../hooks/redux'
import { selectCustomFields } from '../../../../../slices/customFields'

type CustomFieldEntryProps = {
  state: APICustomField
  setState: React.Dispatch<React.SetStateAction<APICustomField>>
}

const CustomFieldEntry: React.FC<CustomFieldEntryProps> = ({
  state,
  setState
}) => {
  const isLegacyField = !!state.options
  const ref = React.useRef(null)
  useDndScrolling(ref, {})
  const { customFields } = useAppSelector(selectCustomFields)

  const [search, setSearch] = React.useState('')

  const fields = React.useMemo(() => {
    let applicableFields

    if (isLegacyField) {
      applicableFields = customFields.filter(
        field =>
          field.object_type === state.object_type &&
          field.field_type !== 'table' &&
          !state.options.includes(field.id)
      )
    } else {
      applicableFields = customFields.filter(
        field =>
          field.object_type === state.object_type &&
          field.field_type !== 'table' &&
          !(state.config.options ?? []).some(opt => opt.val === field.id)
      )
    }

    const query = search.toLocaleLowerCase().trim()
    const searchResults = applicableFields.filter(field =>
      field.title.toLocaleLowerCase().includes(query)
    )

    return searchResults
  }, [isLegacyField, customFields, search, state])

  const baseClasses = useBaseStyles()
  const classes = useStyles()

  const handleMoveOption = React.useCallback(
    (start: string, end: string) => {
      if (start === end) {
        return
      }

      setState(old => {
        const oldItems = cloneDeep(old)
        if (isLegacyField) {
          const oldIdx = oldItems.options.findIndex(v => v === start)
          const newIdx = oldItems.options.findIndex(v => v === end)

          const [removed] = oldItems.options.splice(oldIdx, 1)
          oldItems.options.splice(newIdx, 0, removed)

          return oldItems
        }

        // Calculate indexes
        const oldIdx = oldItems.config.options.findIndex(v => v.val === start)
        const newIdx = oldItems.config.options.findIndex(v => v.val === end)

        const [removed] = oldItems.config.options.splice(oldIdx, 1)
        oldItems.config.options.splice(newIdx, 0, removed)

        return oldItems
      })
    },
    [isLegacyField, setState]
  )

  const handleRemoveOption = React.useCallback(
    (value: string) => {
      setState(old => {
        const oldItems = cloneDeep(old)

        if (isLegacyField) {
          const oldIdx = oldItems.options.findIndex(v => v === value)
          oldItems.options.splice(oldIdx, 1)
          return oldItems
        }

        const oldIdx = oldItems.config.options.findIndex(v => v.val === value)
        oldItems.config.options.splice(oldIdx, 1)
        return oldItems
      })
    },
    [isLegacyField, setState]
  )

  const getFieldName = React.useCallback(
    (id: string) => {
      return (
        customFields.find(field => field.id === id)?.title ?? 'Unknown Field'
      )
    },
    [customFields]
  )

  const handleAdd = React.useCallback(
    (field: LocalCustomField) => {
      setState(old => {
        const updated = cloneDeep(old)
        if (isLegacyField) {
          updated.options.push(field.id)
          return updated
        }
        updated.config.options.push({ val: field.id, conditions: [] })
        return updated
      })
    },
    [isLegacyField, setState]
  )

  return (
    <div className={clsx(baseClasses.row, baseClasses.column)}>
      <div className={baseClasses.label}>Columns</div>

      <div ref={ref} className={baseClasses.scrollable}>
        {isLegacyField
          ? state.options.map((opt, i) => (
              <CustomFieldOption
                key={`${opt}-${i}`}
                id={opt}
                handleMoveOption={handleMoveOption}
                handleRemoveOption={handleRemoveOption}
                isLast={i === state.options.length - 1}
                renderItem={getFieldName}
              />
            ))
          : (state.config?.options ?? []).map((opt, i) => (
              <CustomFieldOption
                key={`${opt}-${i}`}
                id={opt.val}
                handleMoveOption={handleMoveOption}
                handleRemoveOption={handleRemoveOption}
                isLast={i === state.config.options.length - 1}
                renderItem={getFieldName}
              />
            ))}
      </div>

      <input
        placeholder='Search fields'
        value={search}
        onChange={e => setSearch(e.target.value)}
        className={baseClasses.input}
        style={{ marginTop: '0.25rem' }}
      />

      <div className={classes.fields}>
        {fields.length === 0 ? (
          <div>No fields</div>
        ) : (
          <>
            {fields.map(field => (
              <div
                key={field.id}
                onClick={() => handleAdd(field)}
                className={classes.field}
              >
                {field.title}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export default CustomFieldEntry
